/**
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CustomTh from './table/CustomTh';
import CustomTd from './table/CustomTd';
import TableFunction from './table/TableFunction';
import { CustomTextField } from './CustomTextField';
import ButtonSubAction from './buttons/ButtonSubAction';
import {status, STATUS_MODEL} from '../enums/statusEnum';
import CustomDescriptionLabel from './CustomDescriptionLabel';
import {getDateFormat, getImageByExt} from '../Apis/functions';
import CustomImageTable from './CustomImageTable';
import DropDownMenuProfile from './DropDown/DropDownMenuProfile';
import DropDownMenuItem from './DropDown/DropDownMenuItem';
import Tooltip from '@mui/material/Tooltip';
import CustomChip from "./CustomChip";
import {statusColor} from "../enums/statusColorEnum";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from '@mui/icons-material/Upload';

const DocumentStatusThreeTable = ({ data, setSelectedList, handleDocumentValue, documentValue, setDocumentValue,
    handleShowAttachModal, handleDownloadImgApprove, handleDownloadImg, disabled }) => {
    const { t } = useTranslation();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const [tableId, setTableId] = useState("tbl_dstt_table");

    const getThead = () => {
        let header = [<CustomTh tableId={tableId} className='first_head'>{t('name')}</CustomTh>];
        header.push(<CustomTh tableId={tableId}> {t('file')}</CustomTh>);
        header.push(<CustomTh tableId={tableId}> {t('status')}</CustomTh>);
        header.push(<CustomTh tableId={tableId}> {t('approved_by')}</CustomTh>);
        header.push(<CustomTh tableId={tableId}> {t('denied_by')}</CustomTh>);
        header.push(<CustomTh tableId={tableId}> {t('comment')}</CustomTh>);
        return header;
    }

    const getTbody = () => {
        if (!data || data?.length === 0) return {
            body: [],
            data: []
        };

        const customData = data.map((item, index) => ({
            ...item,
            id: item?.id ? item.id : index
        }));

        let body = data.map((item, index) => {
            const updatedAt = new Date(item?.updatedAt);
            const uniqueId = `documentValue-${item?.id || index}`;
            let comment = item?.validationComment?.length > 50;
            let textValue = item?.validationComment?.length > 50 ? `${item.validationComment.slice(0, 50)}...` : (item?.validationComment || '-')

            return (
                <>
                    <CustomTd tableId={tableId} >
                        {item?.companyDocumentPath !== null ? (
                            <div className="grid-display-center">

                                <CustomDescriptionLabel trans={item?.companyDocument?.length > 30 ? `${item.companyDocument.slice(0, 30)}...` : item?.companyDocument} />

                                <ButtonSubAction
                                    id={`btn_dstt_click_to_download_file_${index}`}
                                    name='DOWNLOAD_DOCUMENT'
                                    onClick={() => {
                                        handleDownloadImgApprove(item)
                                    }}
                                />
                            </div>
                        ) : (
                            <div className={'d-flex justify-content-center align-items-center mt-2'}>

                                { (disabled || item?.statusId === STATUS_MODEL.DOCUMENT_IN_VALIDATION || item?.statusId === STATUS_MODEL.DOCUMENT_VALIDATED) ?
                                   <div className={"d-block"}>
                                       <CustomDescriptionLabel trans={item?.companyDocument || '-'}/>
                                       {item.document && <CustomDescriptionLabel trans={item.document} bold={"normal"}/>}
                                   </div>
                                    :
                                    <>
                                        <div className={"w-200px"}>
                                            <CustomTextField
                                                id={uniqueId}
                                                disabled={false}
                                                name={"documentValue"}
                                                value={documentValue[uniqueId] || item?.document || ''}
                                                trans={item?.companyDocument}
                                                type={"text"}
                                                onChange={(e) => {
                                                    setDocumentValue(prev => ({
                                                        ...prev,
                                                        [uniqueId]: e.target.value
                                                    }));
                                                }}
                                            />
                                        </div>
                                        <button
                                            id={`btn_dstt_upload_file_${index}`}
                                            className={'btn-upload-file'}
                                            disabled={
                                                documentValue[uniqueId] === undefined ||
                                                documentValue[uniqueId] === null ||
                                                documentValue[uniqueId].trim() === ''
                                            }
                                            onClick={() => {
                                                handleDocumentValue(item, index);
                                            }}
                                        >
                                            <i className='fa fa-arrow-up' aria-hidden='true'></i>
                                        </button>
                                    </>
                                }
                            </div>
                        )}
                    </CustomTd>

                    <CustomTd tableId={tableId}>
                        { item?.path ?
                            <CustomImageTable tooltip={item?.path} image={getImageByExt(item?.path?.split('.').pop())} placement={"top"}>
                                <span className={"text-ellipsis ml-1 vertical-align-middle"}>{item?.path}</span>
                            </CustomImageTable>
                            :
                            <CustomDescriptionLabel trans={t('no_file')} bold={"normal"} />
                        }
                    </CustomTd>

                    {/* Status with conditional chip */}
                    <CustomTd tableId={tableId}>
                        <CustomChip
                            background={statusColor[item?.statusId]}
                            legend={t(status[item?.statusId]) || '-'}
                        />
                    </CustomTd>

                    <CustomTd tableId={tableId}>
                        {item?.statusId === STATUS_MODEL.DOCUMENT_VALIDATED ?
                            <>
                                <CustomDescriptionLabel trans={`${item?.validatedByName || '-'}`} bold={"normal"} />
                                <CustomDescriptionLabel trans={`${getDateFormat(updatedAt, true)}`} bold={"normal"} />
                            </>
                            :
                             "--"
                        }
                    </CustomTd>

                    <CustomTd tableId={tableId}>
                        {item?.statusId === STATUS_MODEL.DOCUMENT_REJECTED ?
                            <>
                                <CustomDescriptionLabel trans={`${item?.validatedByName || '-'}`} bold={"normal"} />
                                <CustomDescriptionLabel trans={`${getDateFormat(updatedAt, true)}`} bold={"normal"} />
                            </>
                            :
                            "--"
                        }
                    </CustomTd>

                    <CustomTd tableId={tableId} classes='column-w400px'>
                        { comment ?
                            <Tooltip title={textValue} placement="top" arrow disableInteractive>
                                <CustomDescriptionLabel trans={textValue} bold={"normal"} />
                            </Tooltip>
                            :
                            '--'
                        }
                    </CustomTd>
                </>
            );
        });

        return {
            body: body,
            data: customData
        }
    };

    const getActionsButtons = (item, index) => {
        return <>
            {(item?.path || (!disabled && (item?.path == null || item?.statusId === STATUS_MODEL.DOCUMENT_REJECTED))) ?
            <DropDownMenuProfile
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                menuId={`menu-${item.id}`}
            >

                {(!disabled && (item?.path == null || item?.statusId === STATUS_MODEL.DOCUMENT_REJECTED)) &&
                    <DropDownMenuItem
                        index={index}
                        handleClose={handleClose}
                        name={t('attach_file').toUpperCase()}
                        icon={<UploadIcon fontSize='small' />}
                        click={() =>  {
                            handleShowAttachModal(item)
                        }}
                    />
                }

                {item?.path &&
                    <DropDownMenuItem
                        index={index}
                        handleClose={handleClose}
                        name={t('download_attachment').toUpperCase()}
                        icon={<DownloadIcon fontSize='small' />}
                        click={() =>  {
                            handleDownloadImg(item)
                        }}
                    />
                }
            </DropDownMenuProfile>
                : '--'
            }
        </>;
    }

    const { thead, tbody, setTBody, openMenu, handleMenuToggle, handleClose } = TableFunction({
        setSelectedList: setSelectedList,
        data: { content: data },
        getTbody: getTbody,
        getThead: getThead
    });

    useEffect(() => {
        setTBody(getTbody(documentValue));
    }, [documentValue])

    return {
        t, DEV, thead, tbody, tableId,getActionsButtons,
    };

}
export default DocumentStatusThreeTable;
